"use client";
import { useEffect, useState } from "react";
import { ArrowRightIcon, XMarkIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from "@heroicons/react/24/outline";
import SectionTitleComp from "./section-title.comp";
import { useRouter } from "next/navigation";
import { stripHTML } from "@/libs/formater";
import Image from "next/image";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

export default function AyotanampohonComp({
  category,
  categories,
  items,
}: {
  category: string;
  categories: any[];
  items: any[];
}) {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState<any | null>(
    categories[0]
  );
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    const filter = categories.filter((c) => c.id === category);
    if (filter.length > 0) {
      setSelectedCategory(filter[0]);
    }
  }, [category, categories]);

  // Reset zoom and position when modal closes or item changes
  useEffect(() => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [selectedItem]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!categories || categories.length === 0) {
    return (
      <div className="">
        <p className="text-gray-500">Tidak ada kategori tersedia.</p>
      </div>
    );
  }

  return (
    <div className="">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div>
          <SectionTitleComp title="Kategori" />
          <div className="grid grid-flow-row gap-4">
            {categories.map((c) => (
              <button
                key={c.id}
                className="w-full py-2 flex justify-between items-center ps-4 border-l-2 border-gray-400 disabled:border-green disabled:text-green"
                disabled={selectedCategory?.id === c.id}
                onClick={() => router.push(`/updatekarhutla?category=${c.id}`)}
              >
                <span>{c.name} </span>
                <ArrowRightIcon className="size-4 hidden lg:block" />
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <p className="mb-6 font-medium text-lg">{selectedCategory?.name}</p>
          
          {/* Special layout for cara-ambil-bibit-gratis - large image with description */}
          {category === "cara-ambil-bibit-gratis" ? (
            <div className="space-y-8">
              {items && items.length > 0 ? (
                items.map((item: any) => (
                  <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                    {item.imageSrc && (
                      <div className="w-full">
                        <Image
                          src={item.imageSrc}
                          alt="Cara Ambil Bibit"
                          width={1200}
                          height={800}
                          className="object-contain w-full h-auto"
                        />
                      </div>
                    )}
                    {item.description && (
                      <div className="p-6">
                        <div 
                          className="prose prose-lg max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">Belum ada konten tersedia.</p>
              )}
            </div>
          ) : category === "stok-bibit-gratis" ? (
            /* Document list layout for stok-bibit-gratis - card style like eselon1 */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items && items.length > 0 ? (
                items.map((item: any) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl lg:rounded-2xl relative overflow-hidden min-h-32 shadow-sm hover:shadow-md cursor-pointer"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="ml-4 mt-4 w-[65%] pr-2">
                      <p className="text-green font-semibold mb-1 line-clamp-2">{item.title}</p>
                      <p className="text-xs text-gray-500">Klik untuk melihat dokumen</p>
                    </div>
                    {item.iconSrc ? (
                      <img
                        src={item.iconSrc}
                        alt={item.title}
                        className="absolute right-2 bottom-2 h-[60%] object-contain"
                      />
                    ) : (
                      <div className="absolute right-3 bottom-3 w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                        <svg className="w-10 h-10 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h6v6h6v10H6z"/>
                          <path d="M8 14h8v2H8zm0 3h5v2H8z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 col-span-3">Belum ada konten tersedia.</p>
              )}
            </div>
          ) : category === "deskripsi" ? (
            /* Click slider for deskripsi - simple/plain */
            <div className="relative">
              {items && items.length > 0 ? (
                <>
                  {/* Slider content */}
                  <div className="overflow-hidden">
                    <div 
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                      {items.map((item: any) => (
                        <div
                          key={item.id}
                          className="flex-shrink-0 w-full min-h-full"
                        >
                          <div className="bg-white p-6 lg:p-8">
                            {/* Title */}
                            {item.title && (
                              <h3 className="text-xl lg:text-2xl font-bold text-amber-800 mb-6 text-center uppercase">
                                {item.title}
                              </h3>
                            )}
                            
                            {/* Content with image float left */}
                            <div className="prose prose-base max-w-none text-gray-700">
                              {item.imageSrc && (
                                <img 
                                  src={item.imageSrc} 
                                  alt={item.title || "Deskripsi"}
                                  className="float-left mr-6 mb-4 rounded-lg w-64 lg:w-80 object-cover"
                                />
                              )}
                              {item.deskripsi && (
                                <div className="text-justify">
                                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                    {item.deskripsi}
                                  </ReactMarkdown>
                                </div>
                              )}
                              <div className="clear-both"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Simple Navigation */}
                  {items.length > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-6">
                      <button
                        onClick={() => setCurrentSlide(prev => prev > 0 ? prev - 1 : items.length - 1)}
                        className="px-4 py-2 bg-green text-white rounded hover:bg-green/90"
                      >
                        ← Sebelumnya
                      </button>
                      <span className="text-gray-600">
                        {currentSlide + 1} / {items.length}
                      </span>
                      <button
                        onClick={() => setCurrentSlide(prev => prev < items.length - 1 ? prev + 1 : 0)}
                        className="px-4 py-2 bg-green text-white rounded hover:bg-green/90"
                      >
                        Selanjutnya →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-500">Belum ada konten tersedia.</p>
              )}
            </div>
          ) : category === "alamat-dan-kontak-bpdas" ? (
            /* Table layout for alamat-dan-kontak-bpdas with pagination */
            (() => {
              const totalPages = Math.ceil((items?.length || 0) / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const paginatedItems = items?.slice(startIndex, startIndex + itemsPerPage) || [];
              
              return (
                <div>
                  <p className="text-gray-600 mb-4">
                    Berikut adalah daftar alamat dan kontak Balai Pengelolaan Daerah Aliran Sungai (BPDAS) di seluruh Indonesia.
                  </p>
                  <div className="overflow-x-auto">
                    {items && items.length > 0 ? (
                      <>
                        <table className="w-full bg-white rounded-xl overflow-hidden shadow">
                          <thead className="bg-green text-white">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-semibold w-12">No</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Nama BPDAS</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">PIC</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Alamat</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">No Telp</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paginatedItems.map((item: any, index: number) => (
                              <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-600">{startIndex + index + 1}</td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{item.pic}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{item.address}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {item.phone && (
                                    <a href={`tel:${item.phone}`} className="text-green hover:underline">
                                      {item.phone}
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 px-2">
                            <p className="text-sm text-gray-600">
                              Menampilkan {startIndex + 1}-{Math.min(startIndex + itemsPerPage, items.length)} dari {items.length} data
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Sebelumnya
                              </button>
                              <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                  <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`w-8 h-8 text-sm rounded-lg ${
                                      currentPage === page
                                        ? 'bg-green text-white'
                                        : 'bg-white border hover:bg-gray-50'
                                    }`}
                                  >
                                    {page}
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Selanjutnya
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-500">Belum ada konten tersedia.</p>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            /* Default grid layout for other categories */
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-6">
            {items && items.length > 0 ? (
              items.map((item: any) => (
                <div
                  key={item.id}
                  className="hover:opacity-75 cursor-pointer group"
                  onClick={() => {
                    // For berita category, navigate to news detail page
                    if (category === "berita") {
                      router.push(`/news/${item.slug}`);
                    } else {
                      // For other categories (infografis, photo, video, peta), open modal
                      setSelectedItem(item);
                    }
                  }}
                >
                  <div className="rounded-xl bg-gray-500 mb-2 relative overflow-hidden">
                    {item.imageSrc ? (
                      <Image
                        src={item.imageSrc}
                        alt={`${stripHTML(item.title) || "kehutanan"} ${item.id}`}
                        width={200}
                        height={200}
                        className="object-cover w-full h-36"
                      />
                    ) : item.platform === 'instagram' ? (
                      /* Instagram gradient placeholder */
                      <div className="w-full h-36 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                    ) : (
                      <div className="w-full h-36 bg-gray-400 flex items-center justify-center">
                        <span className="text-gray-600">No Image</span>
                      </div>
                    )}
                    {/* Play icon for video items */}
                    {item.isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        {item.platform === 'instagram' ? (
                          <div className="w-12 h-12 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="line-clamp-2 break-words group-hover:text-secondary group-hover:text-green font-medium">
                    {stripHTML(item.title)}
                  </p>
                  {item.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                      {stripHTML(item.description)}
                    </p>
                  )}
                  {item.releaseDate && (
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(item.releaseDate).toLocaleDateString('id-ID', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 col-span-3">Belum ada konten tersedia.</p>
            )}
          </div>
          )}
        </div>
      </div>
      {selectedItem && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-dark bg-opacity-90 p-2 sm:p-4 z-50"
          onClick={() => setSelectedItem(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`relative flex flex-col items-center justify-center ${
              selectedItem.isDocument 
                ? 'w-auto h-auto' 
                : 'w-full h-full max-w-[95vw] max-h-[95vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Document viewer for document items */}
            {selectedItem.isDocument && selectedItem.document ? (
              <div className="relative max-w-2xl w-full bg-white p-5 rounded-xl lg:rounded-2xl mx-4">
                <button
                  className="absolute -top-2 -right-2 text-white bg-gray-800 hover:bg-gray-700 p-1.5 rounded-full z-10"
                  onClick={() => setSelectedItem(null)}
                >
                  <XMarkIcon className="size-5" />
                </button>
                <div className="mb-3">
                  <p className="text-lg font-medium pr-6">{selectedItem.title}</p>
                </div>
                <div className="relative mb-4">
                  <embed
                    src={selectedItem.document}
                    type="application/pdf"
                    className="w-full h-[70vh] min-h-[400px]"
                  />
                </div>
                <div className="w-full text-right">
                  <a
                    href={selectedItem.document}
                    target="_blank"
                    className="bg-green px-5 py-2 rounded-lg text-center text-white hover:bg-green/80"
                  >
                    Unduh
                  </a>
                </div>
              </div>
            ) : (
              <>
              <button
                className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white bg-gray-800 hover:bg-gray-700 p-2 sm:p-3 rounded-full z-10"
                onClick={() => setSelectedItem(null)}
              >
                <XMarkIcon className="size-6 sm:size-8" />
              </button>
              {selectedItem.isVideo && selectedItem.embedUrl ? (
              /* Video Player for video items */
              <div className={`relative w-full ${selectedItem.platform === 'instagram' ? 'max-w-lg' : 'max-w-5xl'} ${selectedItem.platform === 'instagram' ? 'aspect-[9/16] max-h-[80vh]' : 'aspect-video'}`}>
                <iframe
                  src={selectedItem.embedUrl}
                  title={selectedItem.title}
                  className="w-full h-full rounded-xl bg-white"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              /* Image for other items with zoom controls */
              selectedItem.imageSrc && (
                <div className="relative w-full h-full flex flex-col items-center justify-center">
                  {/* Zoom Controls */}
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex gap-2 z-10">
                    <button
                      className="text-white bg-gray-800 hover:bg-gray-700 p-2 rounded-full"
                      onClick={() => {
                        setZoomLevel(prev => Math.min(prev + 0.5, 4));
                      }}
                      title="Perbesar"
                    >
                      <MagnifyingGlassPlusIcon className="size-5 sm:size-6" />
                    </button>
                    <button
                      className="text-white bg-gray-800 hover:bg-gray-700 p-2 rounded-full"
                      onClick={() => {
                        setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
                      }}
                      title="Perkecil"
                    >
                      <MagnifyingGlassMinusIcon className="size-5 sm:size-6" />
                    </button>
                    {zoomLevel !== 1 && (
                      <button
                        className="text-white bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-full text-sm"
                        onClick={() => {
                          setZoomLevel(1);
                          setPosition({ x: 0, y: 0 });
                        }}
                        title="Reset"
                      >
                        {Math.round(zoomLevel * 100)}%
                      </button>
                    )}
                  </div>
                  
                  {/* Draggable Image Container */}
                  <div 
                    className={`overflow-hidden max-w-full max-h-[85vh] ${zoomLevel > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <Image
                      src={selectedItem.imageSrc}
                      alt={selectedItem.title || "Infografis"}
                      width={1920}
                      height={1080}
                      className="object-contain transition-transform duration-100 select-none pointer-events-none"
                      style={{ 
                        transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                        transformOrigin: 'center center'
                      }}
                      draggable={false}
                    />
                  </div>
                </div>
              )
            )}
            
            {/* Caption */}
            {(selectedItem.title || selectedItem.description || selectedItem.releaseDate) && (
              <div className={`${selectedItem.isVideo ? 'mt-4' : 'absolute bottom-4 left-0 right-0'} text-center bg-black bg-opacity-70 py-4 px-6 rounded-lg`}>
                {selectedItem.title && (
                  <p className="text-white text-lg sm:text-xl font-medium">{selectedItem.title}</p>
                )}
                {selectedItem.description && (
                  <p className="text-gray-300 mt-2 text-sm sm:text-base max-w-3xl mx-auto">{selectedItem.description}</p>
                )}
                {selectedItem.releaseDate && (
                  <p className="text-gray-400 mt-2 text-xs sm:text-sm">
                    {new Date(selectedItem.releaseDate).toLocaleDateString('id-ID', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                )}
              </div>
            )}
            </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
